import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PERSON_API_SERVICE_TOKEN } from '@nk/api-lib';
import { CompanyTitleComponent } from '@nk/company-block';
import { PersonCompanyRoleCompositeEntity, SimpleCollection } from '@nk/data-entities-lib';
import { HelpPopoverComponent, SpinnerComponent } from '@nk/ui-lib';
import { DateFormats } from '@nk/utils-lib';
import { catchError, Observable, of, switchMap } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { MonitoringOriginEntityService } from '../../services/monitoring-origin-entity.service';
import { ScreeningCardListDetailsInputData } from '../../utils/screening.utils';

type PersonCompanyRolesInputData = Pick<
  ScreeningCardListDetailsInputData,
  'uuid' | 'type' | 'person_master_uuid' | 'monitoring_origin_entity_company_uuid'
>;

interface PersonCompanyRoleWithConnectionFlag extends PersonCompanyRoleCompositeEntity {
  isConnected?: boolean;
}

@Component({
  selector: 'nk-person-company-roles',
  imports: [
    AsyncPipe,
    TranslateModule,
    RouterLink,
    DatePipe,
    SpinnerComponent,
    CompanyTitleComponent,
    HelpPopoverComponent,
  ],
  templateUrl: './person-company-roles.component.html',
  styleUrls: ['./person-company-roles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PersonCompanyRolesComponent implements OnInit {
  public inputData = input.required<PersonCompanyRolesInputData | null>();
  public screeningConfigUuid = input<string | null>(null);
  public screeningEntityUuid = input<string | null>(null);
  public roles$!: Observable<SimpleCollection<PersonCompanyRoleWithConnectionFlag> | null>;
  public isLoading = signal<boolean>(false);
  public dateFormats = DateFormats;
  public showCompanyColumn = computed(() => !this.inputData()?.monitoring_origin_entity_company_uuid);
  private _personService = inject(PERSON_API_SERVICE_TOKEN);
  private _monitoringOriginEntityService = inject(MonitoringOriginEntityService);

  public ngOnInit(): void {
    const inputData = this.inputData();

    if (inputData && inputData.type === 'person') {
      this.isLoading.set(true);
      const companyUuid = inputData.monitoring_origin_entity_company_uuid ?? undefined;

      // Get connected company UUIDs if screening config and entity are provided
      const connectedCompanyUuids$ = this._getConnectedCompanyUuids();

      this.roles$ = this._personService.personRoleCompositeCollectionByPersonCache
        .get(inputData.uuid, companyUuid, false, 10, 0)
        .pipe(
          switchMap((data) =>
            connectedCompanyUuids$.pipe(
              map((connectedCompanyUuids) => {
                // Filter by company only when company UUID is provided
                const filteredElements = companyUuid
                  ? data.elements.filter((i) => i.company.uuid === companyUuid)
                  : data.elements;

                const processedElements = this._markAndSortConnectedRoles(filteredElements, connectedCompanyUuids);

                return {
                  elements: processedElements,
                  count: processedElements.length,
                  has_more: processedElements.length < data.count,
                };
              }),
            ),
          ),
          tap(() => this.isLoading.set(false)),
        );
    } else {
      this.roles$ = of(null);
      this.isLoading.set(false);
    }
  }

  private _getConnectedCompanyUuids(): Observable<string[] | null> {
    const screeningConfigUuid = this.screeningConfigUuid();
    const screeningEntityUuid = this.screeningEntityUuid();

    if (!screeningConfigUuid || !screeningEntityUuid) {
      return of(null);
    }

    return this._monitoringOriginEntityService.monitoringOriginEntityCompositeCollectionCache
      .get({
        screeningConfigUuid,
        screeningEntityUuid,
        includeHistoryFlag: false,
        orderBy: [],
        limit: 100,
        offset: 0,
      })
      .pipe(
        map((collection) => {
          const connectedCompanyUuids: string[] = [];
          for (const composite of collection.elements) {
            if (
              composite.company?.uuid &&
              composite.monitoring_origin_entity_aggregate?.watchlist_uuids &&
              composite.monitoring_origin_entity_aggregate.watchlist_uuids.length > 0
            ) {
              connectedCompanyUuids.push(composite.company.uuid);
            }
          }
          return connectedCompanyUuids;
        }),
        catchError(() => of(null)),
      );
  }

  private _markAndSortConnectedRoles(
    elements: PersonCompanyRoleCompositeEntity[],
    connectedCompanyUuids: string[] | null,
  ): PersonCompanyRoleWithConnectionFlag[] {
    if (!connectedCompanyUuids || connectedCompanyUuids.length === 0) {
      return elements;
    }

    const markedElements: PersonCompanyRoleWithConnectionFlag[] = elements.map((element) => ({
      ...element,
      isConnected: !!(element.company.uuid && connectedCompanyUuids.includes(element.company.uuid)),
    }));

    // Sort: connected companies first, then others
    markedElements.sort((a, b) => {
      if (a.isConnected && !b.isConnected) return -1;
      if (!a.isConnected && b.isConnected) return 1;
      return 0;
    });

    return markedElements;
  }
}
